import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme, THEMES } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Shield, 
  Coins, 
  Link2, 
  Activity,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Palette,
  Truck,
  Home,
  ShieldCheck,
  UserCheck,
  Briefcase,
  Sliders,
  BarChart3,
  Mail,
  MessageSquareLock,
  SlidersHorizontal,
  Video
} from 'lucide-react';

const navItems = [
  { path: '/hub', label: 'Hub', icon: Home, permission: null },
  { path: '/messages', label: 'Messages', icon: Mail, permission: null },
  { path: '/chat-permissions', label: 'Chat Permissions', icon: MessageSquareLock, permission: 'master' },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'admin' },
  { path: '/training', label: 'Driver Training', icon: Video, permission: 'admin' },
  { path: '/approvals', label: 'Approvals', icon: UserCheck, permission: 'master' },
  { path: '/admin-control', label: 'Admin Control', icon: ShieldCheck, permission: 'master' },
  { path: '/users', label: 'Users', icon: Users, permission: 'admin' },
  { path: '/job-titles', label: 'Job Titles', icon: Briefcase, permission: 'master' },
  { path: '/roles', label: 'Roles', icon: Shield, permission: 'master' },
  { path: '/points', label: 'Points', icon: Coins, permission: 'admin' },
  { path: '/points-config', label: 'Points Config', icon: Sliders, permission: 'master' },
  { path: '/app-stats', label: 'App Stats', icon: BarChart3, permission: 'admin' },
  { path: '/apps', label: 'Connected Apps', icon: Link2, permission: 'master' },
  { path: '/app-settings', label: 'App Access', icon: SlidersHorizontal, permission: 'master' },
  { path: '/events', label: 'Activity Log', icon: Activity, permission: 'admin' },
  { path: '/settings', label: 'Settings', icon: Settings, permission: null },
];

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [showThemes, setShowThemes] = useState(false);
  const { user, logout } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isMasterAdmin = user?.role_id === 'master_admin';
  const isAdmin = isMasterAdmin || user?.role_id === 'admin';

  const filteredNavItems = navItems.filter(item => {
    if (!item.permission) return true;
    if (item.permission === 'master') return isMasterAdmin;
    if (item.permission === 'admin') return isAdmin;
    return false;
  });

  return (
    <aside 
      className={`sidebar transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <div className="flex items-center">
            <img 
              src="/logo-full.png" 
              alt="Leader Ultimate Hub" 
              className="h-12 object-contain drop-shadow-[0_0_8px_rgba(218,165,32,0.5)]"
            />
          </div>
        )}
        {collapsed && (
          <img 
            src="/logo-full.png" 
            alt="Leader Ultimate Hub" 
            className="w-10 h-10 mx-auto object-contain drop-shadow-[0_0_6px_rgba(218,165,32,0.4)]"
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="p-2 space-y-1 mt-4">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.path.slice(1)}`}
            className={({ isActive }) => `
              nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
              ${isActive 
                ? 'active bg-secondary text-foreground font-medium' 
                : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
              }
              ${collapsed ? 'justify-center' : ''}
            `}
          >
            <item.icon size={20} strokeWidth={1.5} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border space-y-3">
        {/* Theme selector */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowThemes(!showThemes)}
            className={`w-full ${collapsed ? 'px-0 justify-center' : 'justify-start'}`}
            data-testid="sidebar-theme-btn"
          >
            <Palette size={20} strokeWidth={1.5} />
            {!collapsed && <span className="ml-3">Theme</span>}
          </Button>
          
          {showThemes && !collapsed && (
            <div className="absolute bottom-full left-0 mb-2 p-2 bg-card border border-border rounded-lg shadow-xl z-50">
              <div className="flex gap-2">
                {Object.values(themes).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setTheme(t.id); setShowThemes(false); }}
                    className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
                      theme === t.id ? 'border-accent' : 'border-border'
                    }`}
                    style={{ background: t.colors.bg }}
                    title={t.name}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.colors.accent }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* User info */}
        {!collapsed && user && (
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.role_id?.replace('_', ' ')}</p>
            </div>
          </div>
        )}

        {/* Logout */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className={`w-full text-destructive hover:text-destructive hover:bg-destructive/10 ${
            collapsed ? 'px-0 justify-center' : 'justify-start'
          }`}
          data-testid="logout-btn"
        >
          <LogOut size={20} strokeWidth={1.5} />
          {!collapsed && <span className="ml-3">Logout</span>}
        </Button>

        {/* Collapse toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          className="w-full"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? (
            <ChevronRight size={16} strokeWidth={1.5} />
          ) : (
            <>
              <ChevronLeft size={16} strokeWidth={1.5} />
              <span className="ml-2">Collapse</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
};
